import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { Form, Button, Modal } from 'react-bootstrap'
import { reportsUrl } from 'urls'
import axios from 'axios'
import { getAxiosDefaultConfig } from 'utils'
import FilePicker from 'sharedComponents/SearchBox/FilePicker'
import { connect } from 'react-redux'
import { getTeamMembers } from 'actions/teamMembersActions'

function normalizeReportTargetEmails(value) {
  if (!value) return []
  if (Array.isArray(value)) return value.filter(Boolean)
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (!trimmed) return []
    if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
      const inner = trimmed.slice(1, -1).trim()
      if (!inner) return []
      return inner.split(',').map((email) => email.trim().replace(/^"|"$/g, '')).filter(Boolean)
    }
    return [trimmed]
  }
  return []
}

const ScheduleReportDialog = connect(
  (store) => ({
    teamMembers: store.teamMembers?.members,
    teamMembersStatus: store.teamMembers?.fetching,
  }),
  { getTeamMembers }
)(({
  isOpen,
  onClose,
  reportId,
  googleDriveAcc,
  setViewToastNotification,
  setToastMessage,
  reportData,
  teamMembers,
  teamMembersStatus,
  getTeamMembers,
  toast = null,
}) => {
  const {
    register,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      report_interval: 'NEVER',
      report_target: null,
      report_target_email: '',
      report_target_emails: '',
      append_file: false,
    },
  })

  const watchReportTarget = watch('report_target')
  const watchReportTargetEmail = watch('report_target_email')
  const watchFileAppend = watch('append_file')

  const [selectedTeamMembers, setSelectedTeamMembers] = useState([])

  const toggleTeamMember = (email) => {
    setSelectedTeamMembers(prev => {
      const index = prev.indexOf(email)
      if (index === -1) {
        return [...prev, email]
      } else {
        return prev.filter(i => i !== email)
      }
    })
  }

  const onSubmit = async (data) => {
    console.log('Form Data:', data)

    const formatData = {
      ...data,
      google_folder_id: folderId,
      google_folder_name: folderName,
      google_file_id: fileId,
      google_file_name: fileName,
    }

    if (watchReportTarget === 'EMAIL') {
      const teamMemberEmails = teamMembers
        .filter(account => selectedTeamMembers.includes(account.shared_with))
        .map(account => account.shared_with)
        .filter(Boolean)
      formatData.report_target_emails = teamMemberEmails
    } else if (watchReportTarget === 'GOOGLE_DRIVE') {
      formatData.report_target_email = data.report_target_email
    }

    await axios.put(`${reportsUrl}/schedule/${reportId}`, formatData, getAxiosDefaultConfig())
    if (!toast) {
      setToastMessage('Report scheduled successfully.')
      setViewToastNotification(true)
    } else {
      toast.success('Report scheduled successfully.')
    }
    onClose()
  }

  const [folderId, setFolderId] = useState('')
  const [folderName, setFolderName] = useState('')
  const [fileId, setFileId] = useState('')
  const [fileName, setFileName] = useState('')

  const setFolderInfo = (folderName, folderId) => {
    setFolderId(folderId)
    setFolderName(folderName)
  }

  const setFileInfo = (fileName, fileId) => {
    setFileId(fileId)
    setFileName(fileName)
  }

  const applyScheduleSettings = useCallback((scheduleData) => {
    if (!scheduleData) return
    setValue('report_interval', scheduleData.report_interval || 'NEVER')
    setValue('report_target', scheduleData.report_target || null)
    setValue('report_target_email', scheduleData.report_target_email || '')
    setValue('append_file', !!scheduleData.google_file_id)
    setFolderId(scheduleData.google_folder_id || '')
    setFolderName(scheduleData.google_folder_name || '')
    setFileId(scheduleData.google_file_id || '')
    setFileName(scheduleData.google_file_name || '')
    setSelectedTeamMembers(normalizeReportTargetEmails(scheduleData.report_target_emails))
  }, [setValue])

  const reportDataRef = useRef(reportData)
  useEffect(() => {
    reportDataRef.current = reportData
  }, [reportData])

  // Load saved schedule settings when the modal opens
  useEffect(() => {
    if (!isOpen || !reportId) return

    const currentReportData = reportDataRef.current
    if (currentReportData) {
      applyScheduleSettings(currentReportData)
    }

    let cancelled = false
    const loadScheduleSettings = async () => {
      try {
        const { data: fetchedReportData } = await axios.get(`${reportsUrl}/${reportId}`, getAxiosDefaultConfig())
        if (!cancelled) {
          applyScheduleSettings(fetchedReportData)
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Error loading report schedule settings:', error)
        }
      }
    }

    loadScheduleSettings()
    return () => {
      cancelled = true
    }
  }, [isOpen, reportId, applyScheduleSettings])

  useEffect(() => {
    if (isOpen) {
      getTeamMembers()
    }
  }, [isOpen, getTeamMembers])

  return (
    <Modal show={isOpen} onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title>Schedule Report</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <div className="tw-space-y-4">
            <Form.Group controlId="report_interval">
              <Form.Label>Report Interval</Form.Label>
              <Form.Control
                as="select"
                {...register('report_interval', { required: 'Interval is required.' })}
                onChange={(e) => setValue('report_interval', e.target.value)}
                defaultValue={reportData?.report_interval || 'NEVER'}
              >
                <option value="DAILY">Daily</option>
                <option value="WEEKLY">Weekly</option>
                <option value="MONTHLY">Monthly</option>
                <option value="NEVER">Never</option>
              </Form.Control>
              {errors.report_interval && <p className="tw-text-red-500 tw-text-sm">{errors.report_interval.message}</p>}
            </Form.Group>

            <Form.Group controlId="report_target">
              <Form.Label>Report Target</Form.Label>
              <Form.Control
                as="select"
                {...register('report_target', { required: 'Target is required.' })}
                onChange={(e) => setValue('report_target', e.target.value)}
                defaultValue={reportData?.report_target || ''}
              >
                <option value="" disabled>
                  Select a target
                </option>
                <option value="EMAIL">Email</option>
                <option value="GOOGLE_DRIVE">Google Drive</option>
              </Form.Control>
              {errors.report_target && <p className="tw-text-red-500 tw-text-sm">{errors.report_target.message}</p>}
            </Form.Group>

            {watchReportTarget === 'EMAIL' && (
              <Form.Group controlId="report_target_emails">
                <Form.Label>Select Team Members to Email</Form.Label>
                <div className="mt-2">
                  {teamMembers && teamMembers.length > 0 ? (
                    teamMembers.map((account, index) => {
                      const selected = selectedTeamMembers.indexOf(account.shared_with) !== -1
                      return (
                        <div key={account.shared_with} className="mb-2">
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={() => toggleTeamMember(account.shared_with)}
                            style={{ cursor: 'pointer' }}
                          />
                          <span
                            style={{
                              opacity: !selected ? 0.5 : 1,
                              fontWeight: !selected ? '300' : '400',
                              cursor: 'pointer',
                              paddingLeft: 16,
                            }}
                            onClick={() => toggleTeamMember(account.shared_with)}
                          >
                            {account.shared_with || account.email}
                          </span>
                        </div>
                      )
                    })
                  ) : teamMembersStatus ? (
                    <p className="text-muted">Loading team members...</p>
                  ) : (
                    <p className="text-muted">No team members available. Please add team members in your account settings.</p>
                  )}
                </div>
                {selectedTeamMembers.length === 0 && (
                  <p className="text-danger text-sm">Please select at least one team member to receive the report.</p>
                )}
              </Form.Group>
            )}

            {watchReportTarget === 'GOOGLE_DRIVE' && (
              <Form.Group controlId="report_target_email">
                <Form.Label>Target Email</Form.Label>
                <Form.Control
                  as="select"
                  {...register('report_target_email', { required: 'Email is required.' })}
                  onChange={(e) => setValue('report_target_email', e.target.value)}
                  defaultValue={reportData?.report_target_email || ''}
                >
                  <option value="" disabled>
                    Select an email
                  </option>
                  {googleDriveAcc.map((item) => (
                    <option key={item.email} value={item.email}>
                      {item.email}
                    </option>
                  ))}
                </Form.Control>
                {errors.report_target_email && (
                  <p className="tw-text-red-500 tw-text-sm">{errors.report_target_email.message}</p>
                )}
              </Form.Group>
            )}
          </div>

          {watchReportTarget === 'GOOGLE_DRIVE' && watchReportTargetEmail &&
          <Form.Group controlId="append_file" className="tw-mt-4">
            <Form.Switch type="checkbox" label="Append Data To Existing File" {...register('append_file')} />
          </Form.Group>}

          {!watchFileAppend && watchReportTarget === 'GOOGLE_DRIVE' && watchReportTargetEmail && (
            <Form.Group controlId="folder_id" className="tw-mt-4">
              <FilePicker
                setFolderInfo={setFolderInfo}
                folderName={folderName}
                viewId="FOLDERS"
                selectedEmail={watchReportTargetEmail}
                hasGoogleDrive={googleDriveAcc.length > 0}
              />
            </Form.Group>
          )}

          {watchFileAppend && watchReportTarget === 'GOOGLE_DRIVE' && watchReportTargetEmail && (
            <Form.Group controlId="folder_id" className="tw-mt-4">
            <FilePicker
              setFileInfo={setFileInfo}
              fileName={fileName}
              viewId="SPREADSHEETS"
              selectedEmail={watchReportTargetEmail}
              hasGoogleDrive={googleDriveAcc.length > 0}
            />
          </Form.Group>
          )}

          <Modal.Footer className="tw-mt-4">
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                reset()
                onClose()
              }}
            >
              Cancel
            </Button>
            <Button type="submit" className="ml-2">
              Save
            </Button>
          </Modal.Footer>
        </Form>
      </Modal.Body>
    </Modal>
  )
})

export default ScheduleReportDialog
